import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useInView, useAnimation } from "framer-motion";
import { 
  ChevronRight, 
  Shield, 
  Lock, 
  CheckCircle2,
  Globe,
  Server,
  FileCheck,
  UserCheck,
  Clock,
  AlertCircle
} from "lucide-react";

import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Helmet } from "react-helmet-async";

const SecurityPage = () => {
  const { t } = useTranslation("common");
  
  // Animation controls for scroll animations
  const controls = useAnimation();
  const securityRef = useRef(null);
  const securityInView = useInView(securityRef, { once: true });
  
  useEffect(() => {
    document.title = "Security | Verdea";
    
    if (securityInView) {
      controls.start("visible");
    }
  }, [controls, securityInView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const securityFeatures = [
    {
      icon: Shield,
      title: "End-to-End Encryption",
      description: "All data is encrypted using AES-256 in transit and at rest, ensuring confidentiality. Our protocols protect against unauthorized access and data breaches."
    },
    {
      icon: Globe,
      title: "EU Data Sovereignty",
      description: "Our EU-based data centers ensure data never leaves European jurisdiction, complying with GDPR and simplifying regulatory compliance."
    },
    {
      icon: UserCheck,
      title: "Access Controls",
      description: "We implement strict role-based access controls and multi-factor authentication to ensure only authorized personnel can access sensitive information."
    },
    {
      icon: FileCheck,
      title: "Compliance Certifications",
      description: "Our security practices are aligned with GDPR, ISO 27001, and other relevant standards to maintain the highest level of data protection."
    },
    {
      icon: Clock,
      title: "Regular Audits",
      description: "We conduct regular security audits and penetration tests to identify and address potential vulnerabilities before they can be exploited."
    },
    {
      icon: AlertCircle,
      title: "Incident Response",
      description: "Our dedicated security team is available 24/7 to respond to any security incidents and ensure the integrity of your data."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Security | Verdea</title>
        <meta name="description" content="Enterprise-grade security to protect your sensitive sustainability data with encryption, access controls, and regulatory compliance" />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-white pt-24 pb-16">
        <div className="absolute inset-0 z-0 opacity-40">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="rgba(15, 61, 43, 0.05)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#1A1F2C] to-[#0F3D2B] bg-clip-text text-transparent">
              Security First Approach
            </h1>
            <p className="text-xl text-slate-700 mb-8 leading-relaxed max-w-3xl mx-auto">
              We take a comprehensive approach to protecting your data, ensuring industry-leading security standards at every level.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  Contact Security Team
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/data-privacy">
                <Button size="lg" variant="outline" className="border-emerald-700 text-emerald-700 hover:bg-emerald-50">
                  Data Privacy
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security Tabs Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Enterprise Security Framework
            </h2>
            <p className="text-lg text-slate-600">
              Our multi-layered security approach ensures comprehensive protection for all your sensitive data
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            {/* Apple-style Tabs */}
            <Tabs defaultValue="data" className="w-full">
              {/* Apple-inspired Tab Navigation */}
              <div className="relative flex justify-center mb-16">
                <div className="absolute bottom-0 w-full border-b border-gray-200"></div>
                <TabsList className="relative bg-transparent border-0 flex space-x-8 p-0 z-10">
                  {[
                    { value: "data", label: "Data Protection" },
                    { value: "access", label: "Access Controls" },
                    { value: "compliance", label: "Compliance" }
                  ].map((tab) => (
                    <TabsTrigger 
                      key={tab.value}
                      value={tab.value}
                      className="px-1 py-2 text-base font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 transition-all duration-200 bg-transparent hover:text-emerald-600 rounded-none focus-visible:outline-none focus-visible:ring-0"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              
              {/* Tab Content with Apple-like Styling */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative z-0"
              >
                <TabsContent value="data" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">Data Protection</h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          All data is encrypted at rest and in transit using AES-256 encryption. Our infrastructure is hosted in EU-based data centers with SOC 2 Type II certification.
                        </p>
                        <ul className="space-y-4">
                          {[
                            "AES-256 encryption for data at rest and in transit",
                            "Private cloud infrastructure with dedicated hardware",
                            "Regular backups and disaster recovery protocols",
                            "Secure vault for storing encryption keys",
                            "Data loss prevention mechanisms"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/dashboard_overview.png" 
                          alt="Security Dashboard" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
                
                <TabsContent value="access" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">Access Controls</h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          We implement strict role-based access controls and multi-factor authentication to ensure only authorized personnel can access sensitive information.
                        </p>
                        <ul className="space-y-4">
                          {[
                            "Role-based access control system",
                            "Multi-factor authentication for all users",
                            "Single Sign-On (SSO) integration",
                            "Comprehensive audit logging for all access events",
                            "Automated session timeouts and device management"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/login_screen.png" 
                          alt="Access Control System" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
                
                <TabsContent value="compliance" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">Compliance Framework</h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          Our security practices are aligned with GDPR, ISO 27001, and other relevant standards to maintain the highest level of data protection.
                        </p>
                        <ul className="space-y-4">
                          {[
                            "GDPR compliance by design and default",
                            "ISO 27001 certified information security",
                            "SOC 2 Type II audited infrastructure",
                            "NIST Cybersecurity Framework alignment",
                            "Regular third-party security audits"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/csrdatlas_knowledge_base.png" 
                          alt="Compliance Dashboard" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
              </motion.div>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section ref={securityRef} className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Comprehensive Security Features
            </h2>
            <p className="text-lg text-slate-600">
              Our security architecture includes multiple layers of protection to safeguard your sensitive data
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid md:grid-cols-3 gap-8"
          >
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-slate-200"
                >
                  <div className="mb-4"><Icon className="h-10 w-10 text-emerald-600" /></div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Secure Your Sustainability Data Today
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join forward-thinking organizations that trust Verdea with their most sensitive sustainability reporting data.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/book-demo">
                <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 min-w-[180px]">
                  Book a Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-600 min-w-[180px]">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SecurityPage;
